'use client';

import { useTranslations } from '@power-core/toolkit';
import { type FunctionComponent, useEffect } from 'react';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useCustomerListInit } from '@/modules/customer/hooks/use-customer-list-init';
import { SharedDocumentsRow } from '@/modules/shared-document/components/shared-documents-row/shared-documents-row';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { LoadingIndicator } from '@/ui-components/loading-indicator/loading-indicator';
import { Paragraph } from '@/ui-components/paragraph/paragraph';
import type { SelectOption } from '@/ui-components/select/types';

import { ProjectRow } from '../project-row/project-row';
import type { AppState } from '@/modules/app-context/types';
import { useAuth } from '@power-core/toolkit';

type ProjectListProps = {
	openIndex: string;
	handleToggleAction: (index?: string | null) => void;
	isPending: boolean;
	projects?: Array<AppState['project']>;
};

export const ProjectList: FunctionComponent<ProjectListProps> = ({ projects, handleToggleAction, openIndex, isPending }) => {
	const t = useTranslations();
	const { auth } = useAuth();
	const { searchProjectValue, setCustomers } = useAppContext();
	const { customers } = useCustomerListInit();

	useEffect(() => setCustomers(customers), [JSON.stringify(customers)]);

	if (auth.status === 'authenticated' && isPending)
		return (
			<Flexbox justify='center' className='mt-5'>
				<LoadingIndicator sizing='large' color='primary' />
			</Flexbox>
		);

	if (!projects?.length && searchProjectValue) {
		return <Paragraph className='text-center mt-5'>{t('T_PROJECTS_NOT_FOUND', { title: searchProjectValue })}</Paragraph>;
	}

	const projectsOptions = projects?.reduce<SelectOption[]>((acc, project) => {
		acc.push({ label: project?.title || '', value: project?.documentId || '' });
		return acc;
	}, []);

	return (
		<div className='h-full bg-n100_grey rounded-lg dark:bg-bg_dark'>
			<div className='h-full overflow-y-auto scrollbar-rounded'>
				{projects?.map((project) => (
					<ProjectRow
						key={project?.documentId}
						project={project}
						isOpen={openIndex === project?.documentId}
						onToggle={() => handleToggleAction(project?.documentId)}
						projectsOptions={projectsOptions}
					/>
				))}
				<SharedDocumentsRow isOpen={openIndex === 'sharedDocuments'} onToggle={() => handleToggleAction('sharedDocuments')} />
			</div>
		</div>
	);
};
